import type { FileRoutesById } from '@routeTree.gen.ts';
import { activeSkin } from '@skins/skins.ts';

/** Značka v tabu — za titulkem stránky, aby bylo poznat, že jde o portál. */
export const TITLE_BRAND = activeSkin.name;

/**
 * Titulky stránek: id routy → i18n klíč (s namespace, protože `defaultNS` je `admin`).
 *
 * Klíč typu `keyof FileRoutesById` znamená, že přejmenovaná nebo smazaná routa spadne
 * při buildu, ne až v produkci. Nová routa se sem musí dopsat — dokud tu není a stránka
 * si titulek nenastaví sama, `PageTitle` v dev režimu zařve do konzole.
 *
 * Nejsou tu admin stránky, které si titulek posílají propem do `AdminLayout` (ten jde
 * přes `usePageTitle` a má přednost), ani routy bez vlastní obrazovky — layout routy
 * (`/c/$orgId/$portalId`, `/s/$orgId/$portalId`) a redirect `/c/$orgId/$portalId/dashboard`.
 *
 * Detailové stránky tu mají obecný titulek ("Detail objednávky"); jméno konkrétní entity
 * si přes `usePageTitle` dosadí sama stránka, až ho načte.
 */
export const PAGE_TITLE_KEYS: Partial<Record<keyof FileRoutesById, string>> = {
  // Přihlášení a veřejné stránky
  '/': 'client:pageTitle.organizationSelect',
  '/login': 'client:auth.login.title',
  '/change-password': 'client:auth.changePassword.title',
  '/lost-password': 'client:auth.lostPassword.title',
  '/lost-login': 'client:auth.lostLogin.title',
  '/reset-password': 'client:auth.resetPassword.title',
  '/demo-pozvanka': 'client:auth.demoInvitation.title',
  '/profile': 'admin:profile.title',
  '/auth': 'admin:pageTitle.authDebug',

  // Rychlé akce z e-mailu (bez přihlášení)
  '/order-action/$code/': 'client:pageTitle.orderAction',
  '/order-action/$code/approve': 'client:pageTitle.orderActionApprove',
  '/order-action/$code/reject': 'client:pageTitle.orderActionReject',
  '/scheduled-order-action/$code/': 'client:pageTitle.scheduledOrderAction',
  '/scheduled-order-action/$code/approve': 'client:pageTitle.scheduledOrderActionApprove',
  '/scheduled-order-action/$code/reject': 'client:pageTitle.scheduledOrderActionReject',
  '/scheduled-order-action/$code/dispatch-now': 'client:pageTitle.scheduledOrderActionDispatchNow',

  // Klient
  '/c/$orgId/$portalId/': 'client:pageTitle.clientDashboard',
  '/c/$orgId/$portalId/catalog/': 'client:catalog.breadcrumb',
  '/c/$orgId/$portalId/catalog/$categoryEcid': 'client:catalog.breadcrumb',
  '/c/$orgId/$portalId/search': 'client:pageTitle.search',
  '/c/$orgId/$portalId/basket': 'client:pageTitle.basket',
  '/c/$orgId/$portalId/order-success/': 'client:pageTitle.orderSuccess',
  '/c/$orgId/$portalId/orders/': 'client:orders.pageTitle',
  '/c/$orgId/$portalId/orders/$orderId': 'client:orders.detailFallback',
  '/c/$orgId/$portalId/scheduled-orders/': 'client:scheduledOrders.title',
  '/c/$orgId/$portalId/scheduled-orders/$scheduledOrderId': 'client:pageTitle.scheduledOrderDetail',
  '/c/$orgId/$portalId/upcoming-goods/': 'client:pageTitle.upcomingGoods',
  '/c/$orgId/$portalId/shopping-list/': 'client:shoppingList.title',
  '/c/$orgId/$portalId/shopping-list/$shoppingListId': 'client:pageTitle.shoppingListDetail',
  '/c/$orgId/$portalId/favourites': 'client:favorites.title',
  '/c/$orgId/$portalId/auctions/': 'client:auctions.pageTitle',
  '/c/$orgId/$portalId/auctions/$auctionId': 'client:pageTitle.auctionDetail',
  '/c/$orgId/$portalId/articles/': 'client:pageTitle.articles',
  '/c/$orgId/$portalId/addresses': 'client:address.page.title',
  '/c/$orgId/$portalId/delivery-windows': 'client:deliveryWindows.title',
  '/c/$orgId/$portalId/custom-products': 'client:customProducts.page.title',
  '/c/$orgId/$portalId/custom-suppliers': 'client:customSuppliers.page.title',
  '/c/$orgId/$portalId/basket-savings/': 'client:basketSavings.title',
  '/c/$orgId/$portalId/support/': 'client:support.page.title',
  '/c/$orgId/$portalId/users': 'client:users.pageTitle',
  '/c/$orgId/$portalId/my-account': 'client:profile.title',
  '/c/$orgId/$portalId/settings': 'client:pageTitle.settings',

  // Dodavatel
  '/s/$orgId/$portalId/': 'client:supplierDashboard.title',
  '/s/$orgId/$portalId/orders/': 'client:supplierOrders.pageTitle',
  '/s/$orgId/$portalId/orders/$orderId': 'client:supplierOrders.detailFallback',
  '/s/$orgId/$portalId/catalog': 'client:supplierCatalog.pageTitle',
  '/s/$orgId/$portalId/products/$productId': 'client:productDetail.title',
  '/s/$orgId/$portalId/prices': 'client:supplierPrices.pageTitle',
  '/s/$orgId/$portalId/delivery-windows': 'client:supplierDeliveryWindows.pageTitle',
  '/s/$orgId/$portalId/my-account': 'client:profile.title',

  // Admin stránky, které si titulek neposílají propem
  '/admin/tasks': 'admin:tasks.title',
  '/admin/image-import/': 'admin:pageTitle.imageImport',
  '/admin/test': 'admin:pageTitle.test',
};
